/**
 * De achtergrondcarousel van een volledig-schermmelding.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-18, FR-19, FR-20
 * Plan: §7.3, §7.4, §6.6
 * Taken: T-3.06, T-3.07, T-3.09
 *
 * ── TIKKEN IS PRIMAIR, SWIPEN IS BONUS ──────────────────────────────────────
 *
 * Dat is geen stijlvoorkeur maar een waarneming over dit publiek: een deel van de bewoners
 * swipet niet betrouwbaar — de vinger blijft plakken, of de beweging is te traag om als
 * gebaar herkend te worden. Daarom staan er altijd twee grote pijlknoppen en tikbare
 * paginastippen, en is de swipe een extra manier om hetzelfde te doen. Een carousel die
 * ALLEEN op swipen werkt, is voor die bewoners een carousel met één pagina.
 *
 * ── GEEN SCROLL-SNAP, GEEN INTERSECTIONOBSERVER ─────────────────────────────
 *
 * ⚠ De ondergrens van de vloot is Chromium 84 (de ES5-guard in `index.html`). CSS
 * scroll-snap en `IntersectionObserver`-paginering zijn daar deels aanwezig en deels
 * anders, en "deels" is op 800+ toestellen met gemengde firmware het slechtste soort
 * antwoord: het werkt op het toestel van de ontwikkelaar en niet op dat van de bewoner.
 * `transform: translateX()` plus Pointer Events doet het overal hetzelfde. Dezelfde les als
 * `AbortSignal.timeout`, dat hier al twee features brak.
 *
 * ── EEN FALEND ITEM MAG DE MELDING NIET MEENEMEN ────────────────────────────
 *
 * (T-3.09.) De tekst IS de melding; de achtergrond is decoratie. Een afbeelding die 404't
 * wordt een effen themavlak met een uitlegregel, en het falen wordt via `onItemError` naar
 * boven gemeld zodat het op `deliveries.last_error` terechtkomt — meetbaar in plaats van
 * bespeculeerd. Er is geen pad waarlangs dit component `null` teruggeeft zolang er items
 * zijn.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResidentNotificationBackgroundItem, CameraStreamUrls } from '@caro/shared/types';
import { CameraStream, type CameraStreamStatus } from './CameraStream';
import { useTranslation } from '../hooks/useTranslation';
import { tf } from '../i18n';

/**
 * Camera-achtergronden zijn AAN sinds fase 4 van `specs/20260831-cameras-als-toestel/`.
 *
 * ── DE CAROUSEL KENT DE KIJKREGEL NIET ──────────────────────────────────────
 *
 * (T-4.03.) Dit component weet niet of er aangebeld wordt, telt geen 180 seconden af en
 * beslist niets. Het roept `requestCameraStream(cameraId)` aan en krijgt óf URL's met een
 * `expiresAt`, óf `null`. Die aanroep komt uit op
 * `POST /api/v1/resident/cameras/:id/stream`, waar de vijf controles al staan: koppeling →
 * site → tenant → geldige niet-ingetrokken vrijgave → niet verlopen.
 *
 * Dat is met opzet de énige plek waar de regel leeft. Een kopie hier zou meteen de zwakste
 * zijn: een gewijzigde bundle, een stilstaande WebView of een tablet met een verkeerd
 * gezette klok negeert hem, en hij zou stilletjes uit de pas gaan lopen met de server.
 *
 * ⚠ Praktisch gevolg: een camera-achtergrond levert alleen beeld op bij een melding die
 * uit een AANBELGEBEURTENIS voortkomt. Bij "raam open in de gang" weigert de server, en
 * deze tegel wordt het effen vlak hieronder. Dat is het bedoelde gedrag, geen defect.
 *
 * De vlag blijft bestaan omdat hij twee dingen doet die geen `if` in de render kan: de
 * bundler kan de `CameraStream`-tak eruit gooien voor een build zonder camera's, en de
 * bestaande tests hebben er een aangrijpingspunt aan.
 */
export const CAMERA_BACKGROUNDS_ENABLED = true;

/** Standaard wachttijd op WHEP voor er naar HLS teruggevallen wordt. */
// 12 s, niet de 5 s van de deurbel: dit zijn losstaande cameras achter een VPN, en het
// pad is koud (`sourceOnDemand`). mediamtx mag er 10 s over doen om de camera op te
// bellen — geeft de client eerder op, dan verliest hij een race die hij niet kan winnen.
// De deurbelpanelen staan op het lokale LAN en houden bewust hun kortere standaardwaarde.
// 20s, niet 12. Gemeten met de verbindingstest op 2026-09-08, over vijftien camera's:
//
//     ICE 0,3 · bron 9,1 · verbinden 0,4 · beeld 1,6
//
// `bron` is het opbellen van een koude camera door mediamtx, en dat kost bij élke camera
// ruim negen seconden. Met een budget van 12s bleef er nauwelijks drie over voor ICE, DTLS
// en het eerste beeld -- en op een tablet met een tragere lijn is dat te weinig. De client
// hoort nooit eerder op te geven dan de server, en die mag er tien over doen.
//
// Zodra `sourceOnDemandCloseAfter` (nu 10m) zijn werk doet, is een tweede blik binnen dat
// venster warm en valt die negen seconden weg. Deze marge is er voor de EERSTE blik.
const CAMERA_WEBRTC_TIMEOUT_MS = 20_000;

/** Hoeveel camerategels tegelijk een stream mogen opzetten. Zie de toelichting bij
 *  `active` hieronder; 1 herstelt het oude gedrag van één tegel tegelijk. */
const MAX_LIVE_CAMERA_SLIDES = 8;

/** Zoveel pixels horizontaal voordat een aanraking als swipe telt en niet als tik. */
const SWIPE_THRESHOLD_PX = 48;

/** Geen animatie boven 200ms (plan §6.6). */
const SLIDE_MS = 200;

export interface NotificationBackgroundCarouselProps {
  items: ResidentNotificationBackgroundItem[];
  /**
   * Een item kon niet getoond worden. Wordt hoogstens één keer per index aangeroepen,
   * zodat een kapotte URL geen stortvloed aan meldingen oplevert.
   */
  onItemError?: (index: number, item: ResidentNotificationBackgroundItem, message: string) => void;
  /**
   * Vraagt een vrijgave voor één camera. `null` = geweigerd, en dat is de NORMALE uitkomst
   * zodra er niet aangebeld wordt. Ontbreekt deze prop, dan blijft elke camerategel een
   * effen vlak — er wordt nooit geraden.
   *
   * In de app is dit `useDoorbellCameras().requestStream`; de carousel zelf doet geen
   * netwerkverkeer, zodat hij zonder API-laag te testen is.
   */
  requestCameraStream?: (cameraId: number) => Promise<CameraStreamUrls | null>;
  /** Alleen voor tests: forceer de reduced-motion-tak zonder matchMedia te moeten stubben. */
  reducedMotionOverride?: boolean;
}

/**
 * Respecteert de systeeminstelling van de bewoner.
 *
 * ⚠ `MediaQueryList.addEventListener` is er pas vanaf Chromium 39 onder die naam en de
 * oudere `addListener` is op deze vloot nog gewoon aanwezig; daarom worden beide
 * geprobeerd. Ontbreekt `matchMedia` helemaal, dan is het antwoord "geen voorkeur" —
 * animeren is dan het bestaande gedrag en niets gaat stuk.
 */
function usePrefersReducedMotion(override?: boolean): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof override === 'boolean') return override;
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof override === 'boolean') return undefined;
    let mql: MediaQueryList | null = null;
    try {
      mql = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    } catch {
      mql = null;
    }
    if (!mql) return undefined;
    const target = mql;
    const handler = () => setReduced(!!target.matches);
    if (typeof target.addEventListener === 'function') {
      target.addEventListener('change', handler);
      return () => target.removeEventListener('change', handler);
    }
    const legacy = target as unknown as {
      addListener?: (cb: () => void) => void;
      removeListener?: (cb: () => void) => void;
    };
    if (typeof legacy.addListener === 'function') {
      legacy.addListener(handler);
      return () => {
        if (typeof legacy.removeListener === 'function') legacy.removeListener(handler);
      };
    }
    return undefined;
  }, [override]);

  return reduced;
}

export function NotificationBackgroundCarousel({
  items,
  onItemError,
  requestCameraStream,
  reducedMotionOverride,
}: NotificationBackgroundCarouselProps) {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const [index, setIndex] = useState(0);
  /** Hoeveel de vinger sinds `pointerdown` verplaatst is, in pixels. */
  const [dragPx, setDragPx] = useState(0);
  const dragging = useRef<boolean>(false);
  const dragStartX = useRef<number | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  /** Per index hoogstens één foutmelding naar boven. */
  const reported = useRef<Set<number>>(new Set());
  const [failed, setFailed] = useState<Record<number, string>>({});

  const count = items.length;
  const safeIndex = count === 0 ? 0 : Math.min(index, count - 1);

  // ── Afbeeldingen voorladen, camera's niet ────────────────────────────────
  //
  // Een afbeelding die pas begint te laden op het moment dat je erheen swipet, is op een
  // trage verbinding een wit vlak. `new Image()` is de goedkoopste voorlader die er is en
  // vraagt geen enkele browser-API die na Chromium 84 is toegevoegd.
  useEffect(() => {
    const loaders: HTMLImageElement[] = [];
    for (const item of items) {
      if (item.kind !== 'image' || !item.url) continue;
      try {
        const img = new Image();
        img.src = item.url;
        loaders.push(img);
      } catch {
        /* voorladen is comfort; het echte laden gebeurt in de <img> hieronder */
      }
    }
    return () => {
      // De referenties loslaten is genoeg; een half geladen afbeelding annuleren kan niet
      // en hoeft niet.
      loaders.length = 0;
    };
  }, [items]);

  const reportError = useCallback(
    (i: number, message: string) => {
      setFailed((prev) => (prev[i] ? prev : { ...prev, [i]: message }));
      if (reported.current.has(i)) return;
      reported.current.add(i);
      const item = items[i];
      if (item && onItemError) {
        try {
          onItemError(i, item, message);
        } catch {
          /* een luisteraar mag de carousel niet slopen — laat staan de melding */
        }
      }
    },
    [items, onItemError],
  );

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      // Geen wrap-around. Op het einde blijven staan is voor dit publiek duidelijker dan
      // onverwacht weer bij de eerste uitkomen — dan lijkt het of er nog pagina's volgen.
      setIndex(Math.max(0, Math.min(count - 1, next)));
      setDragPx(0);
    },
    [count],
  );

  // ── Pointer Events ───────────────────────────────────────────────────────
  //
  // Eén set handlers voor muis, aanraking en pen. `setPointerCapture` zorgt dat een vinger
  // die buiten het element eindigt tóch bij ons uitkomt; zonder dat blijft de track soms
  // half versleept staan.

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (count <= 1) return;
    dragStartX.current = e.clientX;
    dragging.current = true;
    const el = e.currentTarget as HTMLElement & { setPointerCapture?: (id: number) => void };
    try {
      if (typeof el.setPointerCapture === 'function') el.setPointerCapture(e.pointerId);
    } catch {
      /* geen capture beschikbaar — de swipe werkt dan alleen binnen het element */
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartX.current === null) return;
    setDragPx(e.clientX - dragStartX.current);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartX.current === null) return;
    const delta = e.clientX - dragStartX.current;
    dragStartX.current = null;
    dragging.current = false;
    const el = e.currentTarget as HTMLElement & { releasePointerCapture?: (id: number) => void };
    try {
      if (typeof el.releasePointerCapture === 'function') el.releasePointerCapture(e.pointerId);
    } catch {
      /* al losgelaten */
    }
    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
      goTo(safeIndex + (delta < 0 ? 1 : -1));
    } else {
      // Onder de drempel: dit was een tik of een trillende hand, geen gebaar. Terugveren.
      setDragPx(0);
    }
  };

  const trackWidth = trackRef.current ? trackRef.current.clientWidth : 0;
  const offsetPercent = -safeIndex * 100;
  const dragPercent = trackWidth > 0 ? (dragPx / trackWidth) * 100 : 0;

  const trackStyle = useMemo<React.CSSProperties>(
    () => ({
      display: 'flex',
      height: '100%',
      width: '100%',
      transform: `translateX(${offsetPercent + dragPercent}%)`,
      // Bij `prefers-reduced-motion` springt hij naar de volgende pagina in plaats van te
      // schuiven — de knoppen en stippen blijven precies hetzelfde werken. De voorkeur
      // haalt de beweging weg, niet de functie.
      transition:
        reducedMotion || dragging.current ? 'none' : `transform ${SLIDE_MS}ms ease-out`,
      willChange: 'transform',
    }),
    [offsetPercent, dragPercent, reducedMotion],
  );

  if (count === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        backgroundColor: 'var(--caro-card-bg)',
      }}
    >
      <div
        ref={trackRef}
        role="group"
        aria-roledescription="carousel"
        aria-label={tf(t.notifications.backgroundOf, { n: safeIndex + 1, total: count })}
        data-testid="notification-carousel-track"
        style={trackStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {items.map((item, i) => (
          <div
            key={i}
            aria-hidden={i !== safeIndex}
            // `touchAction: pan-y` laat de browser verticaal scrollen en geeft ons het
            // horizontale gebaar, zonder `preventDefault` in een passieve listener — dat
            // laatste levert op een WebView een console vol waarschuwingen op.
            style={{ flex: '0 0 100%', height: '100%', position: 'relative', touchAction: 'pan-y' }}
          >
            <CarouselSlide
              item={item}
              index={i}
              failure={failed[i]}
              onError={reportError}
              // ── ALLE TEGELS TEGELIJK, BEGRENSD ─────────────────────────────────
              //
              // ⚠ Hier stond: precies één tegel actief. De reden blijft geldig -- meerdere
              // gelijktijdige WebRTC-sessies op gemengde firmware is een bekende weg naar
              // geheugendruk en witte schermen (FR-26), en er staat vaak al een gesprek open
              // naast deze melding.
              //
              // Maar het gevolg was dat elke wissel het beeld volledig opnieuw opbouwde, en
              // een koude bron kost 9,1 seconden (gemeten 2026-09-08). Terugbladeren naar een
              // camera die je net gezien had, begon weer van nul.
              //
              // Op beslissing van de eigenaar bouwen ze nu allemaal tegelijk op, net als in
              // `DoorbellCameraView`. De grens is MAX_LIVE_CAMERA_SLIDES: de server kapt de
              // achtergrond al af op acht items, dus dit is een tweede slot op iets wat al
              // begrensd is -- en het getal om te verlagen als er witte schermen terugkomen.
              active={i < MAX_LIVE_CAMERA_SLIDES}
              // Een video speelt alleen op de tegel die je ZIET (specs/20261020-meldingen-
              // media-planning); `active` hierboven gaat over camera's die mogen streamen.
              visible={i === safeIndex}
              requestCameraStream={requestCameraStream}
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <ArrowButton
            direction="prev"
            disabled={safeIndex === 0}
            onClick={() => goTo(safeIndex - 1)}
          />
          <ArrowButton
            direction="next"
            disabled={safeIndex === count - 1}
            onClick={() => goTo(safeIndex + 1)}
          />
          <Dots count={count} active={safeIndex} onSelect={goTo} />
        </>
      )}
    </div>
  );
}

/**
 * Eén pagina.
 *
 * ⚠ Er staat GEEN tekst van de melding op dit vlak. De tekst hoort in het contrastpaneel
 * van `NotificationFullPage` (T-3.02), want op willekeurig beeldmateriaal is geen enkele
 * tekstkleur voorspelbaar leesbaar.
 */
function CarouselSlide({
  item,
  index,
  failure,
  onError,
  active,
  visible = true,
  requestCameraStream,
}: {
  item: ResidentNotificationBackgroundItem;
  index: number;
  failure?: string;
  onError: (index: number, message: string) => void;
  active: boolean;
  visible?: boolean;
  requestCameraStream?: (cameraId: number) => Promise<CameraStreamUrls | null>;
}) {
  const { t } = useTranslation();
  if (failure) {
    // De tekst volgt het soort item. "Afbeelding niet beschikbaar" onder een camerategel
    // stuurt een supportvraag de verkeerde kant op.
    return (
      <SlidePlaceholder
        label={item.kind === 'camera' ? t.notifications.cameraUnavailable : t.notifications.imageUnavailable}
        testId={`carousel-failed-${index}`}
      />
    );
  }

  if (item.kind === 'image') {
    return (
      <img
        src={item.url}
        alt={item.alt || ''}
        data-testid={`carousel-image-${index}`}
        onError={() => onError(index, `image load failed: ${String(item.url).slice(0, 200)}`)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: item.fit === 'contain' ? 'contain' : 'cover',
          display: 'block',
        }}
      />
    );
  }

  if (item.kind === 'video') {
    return <VideoSlide item={item} index={index} active={visible} onError={onError} />;
  }

  // ── De camera-tak ────────────────────────────────────────────────────────
  //
  // Drie redenen om hier NIET aan een stream te beginnen, alle drie eindigend in hetzelfde
  // eerlijke vlak in plaats van een spinner die nooit eindigt:
  //   1. de vlag staat uit (build zonder camera's);
  //   2. deze tegel is niet de zichtbare — één actieve stream tegelijk (FR-26);
  //   3. er is geen `requestCameraStream` meegegeven, dus er is niets om aan te vragen.
  if (!CAMERA_BACKGROUNDS_ENABLED || !active || !requestCameraStream) {
    return (
      <SlidePlaceholder
        label={item.label || t.notifications.cameraUnavailable}
        testId={`carousel-camera-${index}`}
      />
    );
  }

  return (
    <CameraSlide
      item={item}
      index={index}
      onError={onError}
      requestCameraStream={requestCameraStream}
    />
  );
}

/**
 * Een korte video uit de mediabibliotheek (specs/20261020-meldingen-media-planning, FR-2, FR-5).
 *
 * Altijd ZONDER GELUID en in een lus (D-2): het meldingsgeluid en het voorlezen spelen al, en
 * een derde geluidsbron door elkaar is voor een bewoner onverstaanbaar. `muted` + `playsInline`
 * is ook wat een WebView nodig heeft om zonder tik te mogen starten.
 *
 * Alleen de zichtbare tegel speelt; een video die achter een andere tegel doorloopt kost
 * batterij en netwerk voor niets. Lukt het niet (codec, netwerk, verlopen URL), dan hetzelfde
 * vlak als een kapotte afbeelding — de melding blijft (T-3.09).
 */
function VideoSlide({
  item,
  index,
  active,
  onError,
}: {
  item: Extract<ResidentNotificationBackgroundItem, { kind: 'video' }>;
  index: number;
  active: boolean;
  onError: (index: number, message: string) => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (active) {
      // play() geeft op Chromium 84 een Promise; een weigering (autoplay-beleid) is geen
      // fout van de melding — het eerste frame blijft staan.
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => undefined);
    } else {
      el.pause();
    }
  }, [active]);

  return (
    <video
      ref={ref}
      src={item.url}
      muted
      loop
      playsInline
      autoPlay={active}
      preload={active ? 'auto' : 'metadata'}
      aria-label={item.alt || undefined}
      data-testid={`carousel-video-${index}`}
      onError={() => onError(index, `video load failed: ${String(item.url).slice(0, 200)}`)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: item.fit === 'contain' ? 'contain' : 'cover',
        display: 'block',
        background: '#000',
      }}
    />
  );
}

/**
 * Eén camerategel: vraagt een vrijgave en toont er beeld bij, of een vlak.
 *
 * ── DE MELDING VERDWIJNT NOOIT OMDAT DE CAMERA FAALT ────────────────────────
 *
 * (Plan §7.4 van de meldingenspec.) De tekst IS de melding; dit is decoratie. Er is geen
 * enkel pad waarlangs dit component gooit of `null` teruggeeft: een weigering, een
 * netwerkfout en een stream die geen frame levert eindigen alle drie in `SlidePlaceholder`.
 * De reden gaat via `onError` naar boven en belandt op `deliveries.last_error`, zodat
 * "hoe vaak gebeurt dit" een meting wordt in plaats van een vermoeden.
 *
 * ── DE VERVALLADDER ─────────────────────────────────────────────────────────
 *
 * WHEP → (geen frame) → HLS → (geen frame) → effen vlak met uitleg. Die ladder zit al in
 * `CameraStream.tsx` en dat bestand is met opzet ONGEWIJZIGD gebleven (FR-25): het draagt
 * `waitForFirstFrame()`, want een geslaagde SDP-uitwisseling bewijst nog geen beeld.
 */
function CameraSlide({
  item,
  index,
  onError,
  requestCameraStream,
}: {
  item: Extract<ResidentNotificationBackgroundItem, { kind: 'camera' }>;
  index: number;
  onError: (index: number, message: string) => void;
  requestCameraStream: (cameraId: number) => Promise<CameraStreamUrls | null>;
}) {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<CameraStreamUrls | null>(null);
  const [denied, setDenied] = useState(false);
  const { cameraId } = item;

  // ── DE onStatus-PROP MOET STABIEL BLIJVEN ─────────────────────────────────
  //
  // `CameraStream` heeft `onStatus` in de afhankelijkheden van zijn verbindingseffect
  // (CameraStream.tsx:252). Hier stond een inline arrow-functie, dus die prop was bij ELKE
  // render nieuw -- en het volledige scherm hertekent elke seconde voor zijn aftelling.
  //
  // Gevolg, gemeten in productie op 2026-09-08: elke seconde een nieuwe RTCPeerConnection.
  // De mediamtx-log liep vol met honderden sessies, twee tot vijf per seconde, allemaal
  // eindigend op `deadline exceeded while waiting connection` -- de vorige was nog niet
  // rond of de volgende stond er al. Het beeld kwam daardoor nooit.
  //
  // Zelfde fout als in `DoorbellCameraView` (8a238ba), zelfde oplossing: één handler die
  // nooit vervangen wordt, met refs voor wat er per render verandert.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const indexRef = useRef(index);
  indexRef.current = index;

  const handleStatus = useCallback((status: CameraStreamStatus) => {
    // Alleen het falen wordt gemeld. `ready` is de bedoeling en hoort niet in een foutveld;
    // de transportnaam en de ms zitten er wel bij, want "het beeld kwam via HLS na 4
    // seconden" is de meting die de pilot (T-4.04) nodig heeft.
    if (status.phase === 'failed') {
      onErrorRef.current(
        indexRef.current,
        `camera ${cameraId} ${status.transport}: ${status.reason} (${status.ms}ms)`,
      );
    }
  }, [cameraId]);

  useEffect(() => {
    // Voorkomt dat het antwoord van een weggeswipete tegel alsnog een stream opzet op een
    // component dat er niet meer is — precies de situatie waarin er even twee sessies
    // zouden bestaan.
    let cancelled = false;
    setUrls(null);
    setDenied(false);

    requestCameraStream(cameraId)
      .then((result) => {
        if (cancelled) return;
        if (result && result.whepUrl) {
          setUrls(result);
          return;
        }
        // 404 is hier de VERWACHTE uitkomst zodra er niet aangebeld wordt. Dat is geen
        // storing en wordt daarom niet als fout naar boven gemeld; het zou anders elke
        // gewone melding met een camera-item een `last_error` bezorgen.
        setDenied(true);
      })
      .catch(() => {
        if (cancelled) return;
        setDenied(true);
      });

    return () => {
      cancelled = true;
    };
  }, [cameraId, requestCameraStream]);

  if (denied || !urls) {
    return (
      <SlidePlaceholder
        label={item.label || t.notifications.cameraUnavailable}
        testId={`carousel-camera-${index}`}
      />
    );
  }

  return (
    <div data-testid={`carousel-camera-${index}`} style={{ width: '100%', height: '100%' }}>
      <CameraStream
        whepUrl={urls.whepUrl}
        hlsUrl={urls.hlsUrl}
        webrtcTimeout={item.timeoutMs || CAMERA_WEBRTC_TIMEOUT_MS}
        width="100%"
        height="100%"
        borderRadius="0"
        onStatus={handleStatus}
      />
    </div>
  );
}

/** Een effen themavlak met uitleg. Nooit een leeg gat en nooit een eeuwige spinner. */
function SlidePlaceholder({ label, testId }: { label: string; testId?: string }) {
  return (
    <div
      data-testid={testId}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--caro-card-bg)',
        color: 'var(--caro-text)',
        opacity: 0.75,
        fontSize: 20,
        textAlign: 'center',
        padding: 24,
      }}
    >
      {label}
    </div>
  );
}

/**
 * De pijlknop.
 *
 * 88px breed en over de volle hoogte: op een 800x480-kiosk is dat een doel dat je met een
 * trillende hand raakt zonder te hoeven mikken. De emergency-norm uit CLAUDE.md is 80px;
 * deze knoppen liggen daar bewust boven omdat ze tijdens het lezen van een melding
 * gebruikt worden, dus niet met volle aandacht.
 */
function ArrowButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const isPrev = direction === 'prev';
  const style: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 88,
    minHeight: 88,
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.35)',
    color: '#fff',
    fontSize: 40,
    lineHeight: 1,
    cursor: disabled ? 'default' : 'pointer',
    // Uitgeschakeld blijft hij STAAN in plaats van te verdwijnen: een knop die wegspringt
    // verplaatst het doel waar iemand net naar reikte.
    opacity: disabled ? 0.25 : 1,
  };
  if (isPrev) style.left = 0;
  else style.right = 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={isPrev ? t.notifications.previousImage : t.notifications.nextImage}
      data-testid={isPrev ? 'carousel-prev' : 'carousel-next'}
      style={style}
    >
      {isPrev ? '‹' : '›'}
    </button>
  );
}

/**
 * De paginastippen.
 *
 * De stip zelf is 18px (de eis is ≥ 16px), maar het TIKBARE vlak eromheen is 48px — dat is
 * het getal dat telt voor een vinger. Twee maten: één voor het oog, één voor de hand.
 */
function Dots({
  count,
  active,
  onSelect,
}: {
  count: number;
  active: number;
  onSelect: (i: number) => void;
}) {
  const { t } = useTranslation();
  const dots: number[] = [];
  for (let i = 0; i < count; i += 1) dots.push(i);
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 8,
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      {dots.map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={tf(t.notifications.goToImage, { n: i + 1 })}
          aria-current={i === active ? 'true' : undefined}
          data-testid={`carousel-dot-${i}`}
          style={{
            width: 48,
            height: 48,
            padding: 0,
            border: 'none',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'block',
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: i === active ? 'var(--caro-primary)' : 'rgba(255,255,255,0.55)',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.35)',
            }}
          />
        </button>
      ))}
    </div>
  );
}

export default NotificationBackgroundCarousel;
